/**
 * Custom SVG icons
 */

import React from 'react';
import { cn } from '@/lib/utils';

interface IconProps {
  className?: string;
  selected?: boolean;
}

interface LogoIconProps {
  className?: string;
  pulsing?: boolean;
  color?: string;
}

// Primary color glowing keyframes CSS (injected by LogoIcon when pulsing)
const geminiGlowStyles = `
  @keyframes geminiGlow {
    0%, 100% {
      box-shadow:
        0 0 20px rgba(11, 31, 58, 0.5),
        0 0 40px rgba(11, 31, 58, 0.3),
        0 0 60px rgba(11, 31, 58, 0.2);
      transform: scale(1);
    }
    25% {
      box-shadow:
        0 0 25px rgba(11, 31, 58, 0.6),
        0 0 50px rgba(11, 31, 58, 0.4),
        0 0 70px rgba(11, 31, 58, 0.2);
      transform: scale(1.05);
    }
    50% {
      box-shadow:
        0 0 30px rgba(11, 31, 58, 0.5),
        0 0 55px rgba(11, 31, 58, 0.4),
        0 0 80px rgba(11, 31, 58, 0.3);
      transform: scale(1.1);
    }
    75% {
      box-shadow:
        0 0 25px rgba(11, 31, 58, 0.6),
        0 0 45px rgba(11, 31, 58, 0.5),
        0 0 65px rgba(11, 31, 58, 0.3);
      transform: scale(1.05);
    }
  }

  @keyframes geminiGradientRotate {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  @keyframes geminiIconPulse {
    0%, 100% {
      opacity: 1;
      filter: drop-shadow(0 0 8px rgba(11, 31, 58, 0.8));
    }
    33% {
      opacity: 0.9;
      filter: drop-shadow(0 0 12px rgba(11, 31, 58, 0.9));
    }
    66% {
      opacity: 1;
      filter: drop-shadow(0 0 10px rgba(11, 31, 58, 0.8));
    }
  }

  .gemini-glow-container {
    animation: geminiGlow 2s ease-in-out infinite;
  }

  .gemini-gradient-bg {
    background: linear-gradient(
      135deg,
      #0B1F3A 0%,
      #0B1F3A 25%,
      #0B1F3A 50%,
      #0B1F3A 75%,
      #0B1F3A 100%
    );
    background-size: 400% 400%;
    animation: geminiGradientRotate 3s ease infinite;
  }

  .gemini-icon-glow {
    animation: geminiIconPulse 1.5s ease-in-out infinite;
  }
`;

//Logo Icon (Power Button rotated 180°) with optional Gemini-style glow
export const LogoIcon: React.FC<LogoIconProps> = ({ className, pulsing, color = 'currentColor' }) => {
  // Inject styles once
  React.useEffect(() => {
    if (pulsing) {
      const styleId = 'gemini-glow-styles';
      if (!document.getElementById(styleId)) {
        const styleSheet = document.createElement('style');
        styleSheet.id = styleId;
        styleSheet.textContent = geminiGlowStyles;
        document.head.appendChild(styleSheet);
      }
    }
  }, [pulsing]);

  // For pulsing, we use f-dark (brand primary) color with CSS glow animation
  const baseColor = pulsing ? '#0B1F3A' : color;

  return (
    <svg
      viewBox="0 0 24 24"
      className={cn(className, pulsing && "gemini-icon-glow")}
      fill="none"
      style={{ transform: 'rotate(180deg)' }}
    >
      {/* Arc - the curved part of power icon (open at top) */}
      <path
        d="M18.36 6.64a9 9 0 1 1-12.73 0"
        stroke={baseColor}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Vertical line - the center part of power icon */}
      <line
        x1="12"
        y1="2"
        x2="12"
        y2="12"
        stroke={baseColor}
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
};

// New Chat Icon - single chat bubble
export const NewChatIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="21 34 30 24" className={className} fill="none">
    {/* Chat bubble */}
    <rect
      x="21.22"
      y="34.89"
      width="29.54"
      height="18.78"
      rx="2.12"
      ry="2.12"
      fill={selected ? '#0B1F3A' : '#51607A'}
    />
    {/* Bubble pointer */}
    <polygon
      points="24.59 53.69 23.69 56.9 28.42 53.69 24.59 53.69"
      fill={selected ? '#0B1F3A' : '#51607A'}
    />
  </svg>
);

// Chats Icon - overlapping chat bubbles
export const ChatsIcon: React.FC<IconProps> = ({ className, selected }) => (
  <svg viewBox="16 32 40 34" className={className} fill="none">
    {/* Back chat bubble (light gray) */}
    <rect
      x="25.7"
      y="32.56"
      width="29.54"
      height="18.78"
      rx="2.12"
      ry="2.12"
      fill={selected ? 'rgba(11, 31, 58, 0.5)' : '#cacaca'}
    />
    {/* Back bubble pointer */}
    <polygon
      points="51.87 51.36 52.76 54.57 48.04 51.36 51.87 51.36"
      fill={selected ? 'rgba(11, 31, 58, 0.5)' : '#cacaca'}
    />
    {/* Front chat bubble (darker) */}
    <rect
      x="16.74"
      y="42.17"
      width="29.54"
      height="18.78"
      rx="2.12"
      ry="2.12"
      fill={selected ? '#0B1F3A' : '#51607A'}
    />
    {/* Front bubble pointer */}
    <polygon
      points="20.1 60.97 19.21 64.18 23.94 60.97 20.1 60.97"
      fill={selected ? '#0B1F3A' : '#51607A'}
    />
  </svg>
);

// Data URI for use in img onError handlers
export const NO_IMAGE_PLACEHOLDER_URI = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect fill="%231E293B" width="100" height="100" rx="8"/><rect x="25" y="30" width="50" height="40" rx="4" stroke="%2364748B" stroke-width="2" fill="none"/><path d="M25 60 L40 48 L50 55 L65 40 L75 52 L75 66 C75 68.2 73.2 70 71 70 L29 70 C26.8 70 25 68.2 25 66 Z" fill="%2364748B" opacity="0.5"/><circle cx="62" cy="42" r="6" fill="%2364748B" opacity="0.5"/></svg>';
